import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';

/**
 * 자료실 데이터 접근.
 *
 * 자료실은 두 출처(S3 저장소·현장 NAS)를 <b>같은 모양</b>({@link DocumentEntry})으로 조회한다.
 * 연결 실패는 예외가 아니라 값으로 온다({@link DocumentListing.available}=false) — 화면 전체를 막지 않고
 * "연결 안 됨" 만 표시한다. 표준 대·중·소 분류와 NAS 연결은 관리 화면에서 CRUD 한다.
 *
 * 모든 자료는 AI 벡터화 대기열에 올릴 수 있다({@link useEnqueueIndex}) — 실제 임베딩은 에이전트가 가져간다.
 */

export type DocumentCategory = {
  id: string;
  parentId: string | null;
  level: number;
  code: string;
  name: string;
  sortOrder: number;
  active: boolean;
  /**
   * 이 분류가 대신하는 현장(V88). 값이 있으면 <b>현장에서 관리되는 칸</b>이라 분류 관리 화면에서
   * 고치지 못한다 — 현장명을 바꾸면 이 분류의 이름이 따라온다.
   *
   * ⚠ 백엔드가 null 필드를 JSON 에서 빼므로 <b>undefined 로 온다.</b> `== null` 로 비교한다.
   */
  siteId?: string | null;
};

/**
 * ⚠ **비어 있는 값은 `null` 이 아니라 `undefined` 로 온다.** 백엔드가
 * `jackson.default-property-inclusion: non_null` 이라 null 필드를 JSON 에서 아예 뺀다.
 * 그래서 `x === null` 비교는 조용히 거짓이 된다 — **`== null` 로 쓴다**(둘을 함께 받는다).
 * 타입에 `| null` 만 적혀 있어 컴파일러가 잡아 주지 않는 자리다.
 */
export type NasConnection = {
  id: string;
  name: string;
  siteId: string | null;
  /** 본사 부서 NAS 면 그 부서. 현장과 둘 중 하나만 찬다. */
  departmentId: string | null;
  protocol: string;
  host: string;
  port: number | null;
  basePath: string;
  username: string | null;
  domain: string | null;
  hasSecret: boolean;
  lastCheckedAt: string | null;
  lastStatus: string | null;
  sortOrder: number;
  /** 연결 테스트 직후에만 채워지는 상세 원인(영속 안 됨). */
  lastCode?: string | null;
  lastDetail?: string | null;
};

export type IndexStatus = 'NOT_INDEXED' | 'PENDING' | 'INDEXED' | 'FAILED';

export type DocumentEntry = {
  source: 'S3' | 'NAS';
  name: string;
  directory: boolean;
  size: number | null;
  modifiedAt: string | null;
  /**
   * 폴더일 때, 그 아래(하위 폴더 포함) 파일 수. 목록 응답에는 없고 {@link FolderStat} 이 나중에
   * 채운다 — 없으면 아직 안 세었거나 <b>너무 커서 못 센</b> 폴더다.
   */
  fileCount?: number | null;
  /** 하위로 내려가거나 내려받을 때 쓰는 경로(S3 키 또는 NAS 상대경로). */
  ref: string;
  /** 벡터화가 자료를 가리키는 안정 식별자. */
  docRef: string;
  /** S3 는 presigned URL, NAS 는 null(백엔드 스트림 경로를 프론트가 만든다). */
  downloadUrl: string | null;
  indexStatus: IndexStatus;
  /** 색인 실패 사유(FAILED 일 때만). 뱃지에 물려 보여 준다 — 이유가 없으면 사용자가 할 일을 모른다. */
  indexError?: string | null;
  categoryId: string | null;
  categoryCode: string | null;
  categoryName: string | null;
};

export type DocumentListing = {
  source: 'S3' | 'NAS';
  path: string;
  available: boolean;
  message: string | null;
  entries: DocumentEntry[];
};

// ── 표준 분류 ──────────────────────────────────────────────────────────────────

export const documentCategoriesKey = ['document-categories'] as const;

export function useDocumentCategories() {
  return useQuery({
    queryKey: documentCategoriesKey,
    queryFn: () => apiFetch<DocumentCategory[]>('/v1/documents/categories'),
  });
}

export type CategoryInput = { parentId: string | null; code: string; name: string; active?: boolean };

export function useCategoryWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: { method: 'POST' | 'PUT' | 'DELETE'; id?: string; body?: CategoryInput }) =>
      apiFetch<unknown>(`/v1/documents/categories${request.id ? `/${request.id}` : ''}`, {
        method: request.method,
        body: request.body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentCategoriesKey }),
  });
}

/**
 * 분류 코드를 바꾸면 저장소에서 <b>몇 건이 움직이는가</b>. 아무것도 바꾸지 않는다 —
 * 화면이 «폴더도 옮길까요» 를 묻기 전에 숫자를 채우는 데 쓴다.
 */
export type CategoryStorageImpact = {
  /** 저장소 연동이 꺼져 있으면 옮길 수 없다. 그때는 묻지 않고 그 사실만 적는다. */
  storageEnabled: boolean;
  objects: number;
  /** DB 는 가리키는데 실물이 없는 수. 0 이 아니면 사람이 봐야 한다. */
  missing: number;
};

export type CategoryStorageReport = {
  planned: number;
  copied: number;
  skipped: number;
  missing: number;
  removed: number;
  rowsUpdated: number;
};

/** 화면이 직접 부른다(react-query 캐시에 담지 않는다) — 저장 직전 한 번 세어 보는 값이다. */
export function fetchCategoryStorageImpact(id: string, toCode: string) {
  return apiFetch<CategoryStorageImpact>(
    `/v1/documents/categories/${id}/storage-impact?toCode=${encodeURIComponent(toCode)}`,
  );
}

/**
 * 저장소 폴더·경로를 새 코드로 옮긴다. <b>코드를 고친 뒤</b>에 부른다 — 분류 행에는 이미 새 코드가
 * 들어 있어서, 옛 코드는 화면이 들고 있다가 넘겨야 한다.
 */
export function relocateCategoryStorage(id: string, fromCode: string) {
  return apiFetch<CategoryStorageReport>(
    `/v1/documents/categories/${id}/relocate-storage?fromCode=${encodeURIComponent(fromCode)}`,
    { method: 'POST' },
  );
}

// ── NAS 연결 ───────────────────────────────────────────────────────────────────

export const nasConnectionsKey = ['nas-connections'] as const;

/**
 * <b>관리용</b> 연결 목록 — `settings_nas` 권한이 필요하고 host·계정까지 싣는다.
 * 연결을 등록·수정하는 화면(`NasManager`)만 쓴다.
 */
export function useNasConnections(enabled = true) {
  return useQuery({
    queryKey: nasConnectionsKey,
    queryFn: () => apiFetch<NasConnection[]>('/v1/documents/nas'),
    // 관리 권한이 없는 사람에게는 이 요청 자체가 403 이다 — 부르는 자리에서 꺼 둘 수 있어야
    // 「연결 판」 처럼 <b>둘 다 오는 화면</b>이 조회 권한자에게 실패한 질의를 하나씩 달고 다니지 않는다.
    enabled,
  });
}

/** 자료 조회가 고를 수 있는 연결 한 줄. 접속 정보(host·계정)는 오지 않는다. */
export type NasBrowseConnection = {
  id: string;
  name: string;
  siteId: string | null;
  departmentId: string | null;
  basePath: string;
  lastStatus: string | null;
  sortOrder: number;
};

export const browsableNasKey = ['nas-browsable'] as const;

/**
 * <b>조회용</b> 연결 목록 — `docs_browse` 권한이면 되고, 서버가 <b>담당 현장으로 걸러</b> 준다.
 *
 * <p>예전에는 조회 화면도 위의 관리 목록을 썼다. 그래서 폴더를 읽는 것은 `docs_browse` 인데
 * <b>목록만 관리자</b>여서, 현장 사람이 자기 현장 NAS 를 못 봤다(2026-08-19 · DOC-49 §2.4).
 */
export function useBrowsableNasConnections() {
  return useQuery({
    queryKey: browsableNasKey,
    queryFn: () => apiFetch<NasBrowseConnection[]>('/v1/documents/browse/nas/connections'),
  });
}

export type NasConnectionInput = {
  name: string;
  /**
   * 열람 범위를 정하는 칸 ①. 현장 NAS 면 그 현장.
   * `departmentId` 와 <b>둘 중 하나</b>는 있어야 한다 — 둘 다 비면 전 직원에게 열린다.
   */
  siteId: string | null;
  /** 열람 범위를 정하는 칸 ②. 본사 부서 NAS 면 그 부서. 그 부서 사람만 목록에서 보고 연다. */
  departmentId: string | null;
  protocol: string;
  host: string;
  port: number | null;
  basePath: string;
  username: string | null;
  /** 평문. 서버가 암호화해 저장한다. 수정 시 비우면 기존 유지. */
  password: string | null;
  domain: string | null;
};

/**
 * 연결 등록·수정·삭제.
 *
 * <p>등록·수정은 <b>저장된 줄을 돌려준다</b>(삭제는 null). 새로 만든 연결의 id 가 그 안에 있어야
 * 화면이 곧바로 「연결 테스트」 를 걸 수 있다 — 시험은 id 로만 부를 수 있기 때문이다.
 */
export function useNasWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: { method: 'POST' | 'PUT' | 'DELETE'; id?: string; body?: NasConnectionInput }) =>
      apiFetch<NasConnection | null>(`/v1/documents/nas${request.id ? `/${request.id}` : ''}`, {
        method: request.method,
        body: request.body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nasConnectionsKey });
      // 조회용 목록도 함께 새로 받는다 — 이름을 고치거나 연결을 지운 뒤 <b>고르는 판</b>이 옛 이름을
      // 들고 있으면 방금 한 일이 안 된 것으로 읽힌다(그 판에서도 고칠 수 있게 된 뒤로는 흔한 일이다).
      queryClient.invalidateQueries({ queryKey: browsableNasKey });
    },
  });
}

/**
 * NAS <b>폴더 ↔ 표준 분류</b> 매핑. 이것이 없으면 그 폴더의 자료는 <b>한 건도 색인되지 않는다.</b>
 *
 * <h2>왜 필수인가</h2>
 * 색인 워커가 만드는 문서 행({@code document_files})의 {@code category_id} 는 NOT NULL 이고,
 * S3 는 키(`docs/{현장}/{분류}/…`)에서 분류를 되짚을 수 있지만 <b>NAS 경로에는 그런 규칙이 없다.</b>
 * 그래서 사람이 폴더에 분류를 걸어 주는 것이 유일한 근거고, 없으면 서버가 거절한다
 * (`DOC_CATEGORY_UNRESOLVED`). 저장소 훑기를 켜면 그 폴더의 파일이 통째로 FAILED 로 쌓인다.
 *
 * <h2>경로는 basePath 를 뺀 상대경로다</h2>
 * 목록이 준 {@code entry.ref} 를 그대로 보낸다({@code 도면}, {@code /현장자료/도면} 아님).
 * 서버가 파일의 분류를 찾을 때 <b>그 파일의 상위 폴더 경로와 문자열로 맞대기</b> 때문에,
 * 앞에 basePath 를 붙이면 영원히 안 맞는다 — 매핑은 걸려 있는데 색인은 거절되는 상태가 된다.
 *
 * <p>하위 폴더마다 다시 걸 필요는 없다. 서버가 <b>상위로 거슬러 올라가며 가장 가까운 매핑</b>을 쓴다.
 */
export function useSetNasFolderCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectionId,
      relativePath,
      categoryId,
    }: {
      connectionId: string;
      relativePath: string;
      /** null 이면 매핑 해제. */
      categoryId: string | null;
    }) =>
      apiFetch<void>(`/v1/documents/nas/${connectionId}/folder-category`, {
        method: 'PUT',
        body: { relativePath, categoryId },
      }),
    // 목록을 다시 읽어야 방금 건 분류가 칩에 나타난다. 안 읽으면 눌렀는데 아무 일도 없어 보인다.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['docs-browse-nas'] }),
  });
}

export function useNasTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<NasConnection>(`/v1/documents/nas/${id}/test`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: nasConnectionsKey }),
  });
}

// ── 조회 ───────────────────────────────────────────────────────────────────────

/**
 * 조회 한 건의 <b>키와 호출을 한 자리에 묶어 둔다</b>. 목록(가운데)과 폴더 트리(왼쪽)가 같은 폴더를
 * 서로 다른 키로 읽으면 같은 것을 두 번 받고, 한쪽만 새로 고쳐져 트리와 목록이 어긋난다 —
 * 트리에서 폴더를 눌렀을 때 목록이 곧바로 뜨는 것도 이 캐시를 공유하기 때문이다.
 */
export function s3BrowseQuery(prefix: string) {
  return {
    queryKey: ['docs-browse-s3', prefix] as const,
    queryFn: () =>
      apiFetch<DocumentListing>(`/v1/documents/browse/s3${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`),
  };
}

/**
 * 하위 폴더의 파일 수·용량·마지막 수정일.
 *
 * <h2>왜 목록과 따로 받나</h2>
 * S3 에 폴더는 없다 — 목록이 폴더처럼 보여 주는 것은 키 안의 `/` 를 접은 결과라 <b>크기도 날짜도
 * 애초에 값이 없다</b>. 세려면 그 아래를 실제로 훑어야 하는데, 그것을 목록 응답에 넣으면 무거운
 * 폴더에서 <b>목록 자체가 늦어진다</b>. 따로 받아 나중에 채우면 목록은 즉시 뜬다.
 *
 * <p>서버는 상한까지만 훑고 <b>확실히 다 센 폴더만</b> 돌려준다. 그래서 결과에 없는 폴더가 있는 것이
 * 정상이다(루트처럼 아래가 무거운 자리) — 그 칸은 종전처럼 '—' 로 남는다.
 */
export type FolderStat = {
  key: string;
  fileCount: number;
  sizeBytes: number;
  modifiedAt: string | null;
};

export function s3FolderStatsQuery(prefix: string) {
  return {
    queryKey: ['docs-folder-stats-s3', prefix] as const,
    queryFn: () =>
      apiFetch<FolderStat[]>(
        `/v1/documents/browse/s3/folder-stats${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`,
      ),
  };
}

export function useS3FolderStats(prefix: string, enabled = true) {
  return useQuery({ ...s3FolderStatsQuery(prefix), enabled, staleTime: 60_000 });
}

/**
 * 저장소 전체의 사용량과 총 용량 — 나무 맨 아래 칸.
 *
 * <h2>두 숫자의 출처가 다르다</h2>
 * `usedBytes` 는 버킷 안 오브젝트를 다 더한 값이고, `totalBytes` 는 MinIO 관리 API 가 말하는
 * 드라이브(=PV) 총량이다. 관리 API 를 못 읽는 배포에서는 `totalBytes` 가 <b>안 온다</b>
 * (백엔드가 null 필드를 JSON 에서 뺀다 — `== null` 로 본다). 그때는 사용량만 세운다.
 *
 * <p>서버가 몇 분치를 캐시하므로 이 조회는 싸다. 그래도 `staleTime` 을 길게 두는 것은,
 * 폴더를 옮겨 다닐 때마다 같은 값을 다시 받아 올 이유가 없기 때문이다.
 */
export type StorageUsage = {
  usedBytes: number;
  objectCount: number;
  /** 빗장에 걸려 <b>덜 센</b> 값인가. true 면 화면이 「이상」으로 말해야 한다. */
  partial: boolean;
  /** 자료실 몫의 총량 — 선언한 PV 크기(서버가 PVC 에서 읽는다). 어느 출처도 답하지 못하면 오지 않는다. */
  totalBytes?: number | null;
  /** 파티션 전체 크기(실측). 못 읽으면 오지 않는다. */
  driveTotalBytes?: number | null;
  /** 파티션이 실제로 쓰는 양 — <b>이웃 PV 가 채운 것까지</b> 들어 있다. */
  driveUsedBytes?: number | null;
  measuredAt: string;
};

export function useS3StorageUsage(enabled = true) {
  return useQuery({
    queryKey: ['docs-storage-usage-s3'] as const,
    queryFn: () => apiFetch<StorageUsage | null>('/v1/documents/browse/s3/usage'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * 저장공간 분석(DOC-124 · SUPER_ADMIN 전용) — S3 와 연결된 NAS 를 서버가 재어 DB 에 쌓은 값.
 *
 * ⚠ null 칸은 JSON 에서 빠져 `undefined` 로 온다. `== null` 로 본다.
 */
export type StorageAnalysisPoint = {
  measuredAt: string;
  status: 'OK' | 'FAILED';
  totalBytes?: number | null;
  usedBytes?: number | null;
  objectCount?: number | null;
  partial: boolean;
  /** 잰 시점에 DB(document_files)에 등록된 이 저장공간의 파일 크기 합·건수. */
  registeredBytes: number;
  registeredCount: number;
  /** 실패 사유 — 이 화면은 관리자만 본다. */
  errorMessage?: string | null;
};

export type StorageAnalysisSource = {
  kind: 'S3' | 'NAS';
  sourceId?: string | null;
  name: string;
  protocol?: string | null;
  /** 마지막 측정(성공·실패 무관). 한 번도 안 쟀으면 오지 않는다. */
  latest?: StorageAnalysisPoint | null;
  /** 성공한 측정만, 하루 한 점. */
  history: StorageAnalysisPoint[];
  /** NAS 관리자 계정 이름(DOC-142). 없으면 오지 않는다. 비밀번호는 어디에도 실리지 않는다. */
  adminUsername?: string | null;
  /** 마지막 상태 점검(DOC-142). 관리자 계정이 없거나 아직 안 쟀으면 오지 않는다. */
  health?: NasHealth | null;
};

export type NasHealthDisk = { name?: string | null; model?: string | null; status?: string | null; temp?: number | null; problem: boolean };
export type NasHealthVolume = { name?: string | null; status?: string | null; fsType?: string | null; problem: boolean };
export type NasHealthShare = { name: string; snapshotCount: number; lastSnapshotAt?: string | null; immutable: boolean };

export type NasHealth = {
  measuredAt: string;
  status: 'OK' | 'FAILED';
  model?: string | null;
  dsmVersion?: string | null;
  systemTemp?: number | null;
  diskCount?: number | null;
  diskProblemCount?: number | null;
  maxDiskTemp?: number | null;
  volumeCount?: number | null;
  volumeProblemCount?: number | null;
  shareCount?: number | null;
  snapshotShareCount?: number | null;
  immutableShareCount?: number | null;
  lastSnapshotAt?: string | null;
  detail?: {
    disks?: NasHealthDisk[];
    volumes?: NasHealthVolume[];
    shares?: NasHealthShare[];
    errors?: string[];
  } | null;
  errorMessage?: string | null;
};

export type StorageAnalysis = {
  measuring: boolean;
  sources: StorageAnalysisSource[];
};

const STORAGE_ANALYSIS_KEY = ['docs-storage-analysis'] as const;

export function useStorageAnalysis(enabled: boolean, days = 90) {
  return useQuery({
    queryKey: [...STORAGE_ANALYSIS_KEY, days] as const,
    queryFn: () => apiFetch<StorageAnalysis>(`/v1/admin/storage-usage?days=${days}`),
    enabled,
    staleTime: 60_000,
    // 측정이 뒤에서 도는 동안만 다시 묻는다. 끝나면 멈춘다.
    refetchInterval: (query) => (query.state.data?.measuring ? 3_000 : false),
  });
}

export function useMeasureStorage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ started: boolean }>('/v1/admin/storage-usage/measure', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STORAGE_ANALYSIS_KEY }),
  });
}

/** NAS 관리자 계정 저장 — 서버가 실제로 로그인해 보고 저장한다. 구글 OTP 6자리를 함께 보낸다(DOC-142). */
export function useSaveNasAdminAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, username, password, code }: { id: string; username: string; password: string; code: string }) =>
      apiFetch<void>(`/v1/admin/storage-usage/nas/${id}/admin-account`, {
        method: 'POST',
        body: { username, password, code },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STORAGE_ANALYSIS_KEY }),
  });
}

/** 정리 보고서(DOC-142 §7) — 등록된 파일 기준, 개인 자료 제외. 읽기만 한다. */
export type CleanupFile = {
  id: string;
  storedName: string;
  contentHash?: string | null;
  sizeBytes?: number | null;
  createdAt: string;
  s3Key?: string | null;
};

export type CleanupReport = {
  duplicateGroupCount: number;
  wastedBytes: number;
  duplicates: Array<{ contentHash: string; fileCount: number; sizeBytes?: number | null; wastedBytes: number; files: CleanupFile[] }>;
  largest: CleanupFile[];
  withoutHash: number;
};

export function useStorageCleanup(enabled: boolean) {
  return useQuery({
    queryKey: ['docs-storage-cleanup'] as const,
    queryFn: () => apiFetch<CleanupReport>('/v1/admin/storage-usage/cleanup'),
    enabled,
    staleTime: 60_000,
  });
}

/** S3 → NAS 백업(DOC-142 · V609). null 칸은 오지 않는다. */
export type StorageBackupRun = {
  startedAt: string;
  finishedAt?: string | null;
  status: 'RUNNING' | 'OK' | 'PARTIAL' | 'FAILED';
  scannedCount: number;
  copiedCount: number;
  copiedBytes: number;
  skippedCount: number;
  failedCount: number;
  errorMessage?: string | null;
};

export type StorageBackupStatus = {
  enabled: boolean;
  connectionId?: string | null;
  connectionName?: string | null;
  folder: string;
  running: boolean;
  lastRun?: StorageBackupRun | null;
};

const STORAGE_BACKUP_KEY = ['docs-storage-backup'] as const;

export function useStorageBackup(enabled: boolean) {
  return useQuery({
    queryKey: STORAGE_BACKUP_KEY,
    queryFn: () => apiFetch<StorageBackupStatus>('/v1/admin/storage-usage/backup'),
    enabled,
    refetchInterval: (query) => (query.state.data?.running ? 5_000 : false),
  });
}

export function useSaveStorageBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean; connectionId: string | null; folder: string; code: string }) =>
      apiFetch<StorageBackupStatus>('/v1/admin/storage-usage/backup/config', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STORAGE_BACKUP_KEY }),
  });
}

export function useRunStorageBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ started: boolean }>('/v1/admin/storage-usage/backup/run', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STORAGE_BACKUP_KEY }),
  });
}

export function useRemoveNasAdminAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, code }: { id: string; code: string }) =>
      apiFetch<void>(`/v1/admin/storage-usage/nas/${id}/admin-account/remove`, { method: 'POST', body: { code } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STORAGE_ANALYSIS_KEY }),
  });
}

/**
 * 현장 자료를 현장 폴더 하나(`docs/site/{현장명}/`)로 모으는 <b>일회성 정리</b>.
 *
 * <p>버튼을 화면에 둔 이유: 이 일은 서버가 알아서 하면 안 된다(저장소 배치와 여러 표의 키를 한꺼번에
 * 바꾼다 — 기동할 때마다 도는 스위치로 두면 이 코드가 들어간 모든 배포가 자기도 모르게 옮긴다).
 * 그렇다고 curl 로만 열어 두면 <b>아무도 누르지 않아 영영 반영되지 않는다.</b> 그래서 사람이
 * 계획을 눈으로 보고 누르는 자리를 만든다.
 */
export type SiteFolderMove = { displayName: string; sourceKey: string; targetKey: string };

export type SiteFolderReport = {
  dryRun: boolean;
  planned: number;
  copied: number;
  skipped: number;
  /** 원본이 저장소에 없어 손대지 않은 수. <b>0 이 아니면 사람이 봐야 한다.</b> */
  missing: number;
  rowsUpdated: number;
  sites: Array<{
    site: string;
    targetPrefix: string;
    planned: number;
    copied: number;
    skipped: number;
    missing: number;
    rowsUpdated: number;
    /** 훑기 상한에 걸려 이 현장 자료를 다 보지 못했다. 다시 누르면 이어서 한다. */
    truncated: boolean;
    plan: SiteFolderMove[];
  }>;
  /**
   * 어느 현장 것도 아니어서 <b>손대지 않은 폴더</b>들(현장 이름을 바꾼 뒤의 옛 폴더 · 손으로 만든 자리).
   * "옮겼는데 그 파일이 없다" 의 답은 대개 여기 있다.
   */
  leftovers: string[];
};

/**
 * 개인 자료를 그 사람의 폴더(`mypage/{이름_생년월일}/`)로 모으는 <b>일회성 정리</b>.
 * 현장 폴더 정리와 같은 규칙이다 — 사람이 계획을 보고 누른다(기동 훅이 없다).
 */
export type PersonalFolderReport = {
  dryRun: boolean;
  planned: number;
  copied: number;
  skipped: number;
  missing: number;
  rowsUpdated: number;
  /** 손댈 것이 있는 사람만 실린다 — 전 직원을 실으면 인력 명부가 되어 봐야 할 줄이 묻힌다. */
  people: Array<{
    person: string;
    targetPrefix: string;
    planned: number;
    copied: number;
    skipped: number;
    missing: number;
    rowsUpdated: number;
    plan: SiteFolderMove[];
  }>;
};

export function usePersonalFolderRelocate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      apiFetch<PersonalFolderReport>(`/v1/admin/personal-folders/relocate?dryRun=${dryRun}`, { method: 'POST' }),
    onSuccess: (report) => {
      if (report.dryRun) return;
      void queryClient.invalidateQueries({ queryKey: ['docs-browse-s3'] });
      void queryClient.invalidateQueries({ queryKey: ['docs-folder-stats-s3'] });
    },
  });
}

export function useSiteFolderRelocate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      apiFetch<SiteFolderReport>(`/v1/admin/site-folders/relocate?dryRun=${dryRun}`, { method: 'POST' }),
    onSuccess: (report) => {
      if (report.dryRun) return;
      // 옮긴 뒤에는 목록이 달라져 있다. 안 비우면 방금 만든 폴더가 화면에 안 나타나 "안 됐다" 로 읽힌다.
      void queryClient.invalidateQueries({ queryKey: ['docs-browse-s3'] });
      void queryClient.invalidateQueries({ queryKey: ['docs-folder-stats-s3'] });
    },
  });
}

/**
 * 자료 정리 — 이미 쌓인 파일을 <b>지금의 규칙</b>대로 다시 앉힌다(경로·이름).
 *
 * <p>계획과 실행이 <b>같은 요청 모양</b>을 쓴다. 서버가 화면이 보낸 파일 목록을 그대로 옮기지 않고
 * 같은 입력으로 계획을 다시 세우기 때문이다 — 그 사이 저장소가 바뀌었어도 엉뚱한 파일을 건드리지 않는다.
 */
export type CleanupRequest = {
  /**
   * 정리할 <b>파일들</b>(S3 키) — 사람이 목록·트리에서 체크한 것. 있으면 이쪽만 본다.
   * 고른 것이 곧 대상이라 결과가 눈에 보인 그대로다.
   */
  keys: string[];
  /** 정리할 폴더들(S3 키 접두어). {@link keys} 가 비었을 때만 쓴다. 둘 다 비면 아무것도 하지 않는다. */
  prefixes: string[];
  recursive: boolean;
  /** 비우면 경로에서 현장을 판단하고, 못 읽으면 그 파일은 건너뛴다. */
  siteId: string | null;
  /** 비우면 경로에서 분류를 판단하고, 못 읽으면 그 파일은 건너뛴다. */
  categoryId: string | null;
  /**
   * 이름의 날짜 토큰을 무엇으로 채울지. `MODIFIED` 는 <b>파일명에 적힌 날짜가 먼저</b>이고
   * (`계약서_240824.pdf` → 2024-08-24) 없을 때만 저장소 수정일이다 — 수정일은 파일이 옮겨
   * 다니면 바뀌지만 이름의 날짜는 사람이 적어 둔 사실이다.
   */
  dateSource: 'MODIFIED' | 'TODAY' | 'NONE';
  /** 끄면 같은 파일이 두 벌 남는다(되돌릴 근거는 생긴다). */
  deleteSource: boolean;
};

export type CleanupPlanItem = {
  sourceKey: string;
  targetKey: string | null;
  status: 'RENAME' | 'UNCHANGED' | 'SKIPPED';
  reason?: string | null;
};

export type CleanupPlan = {
  scanned: number;
  rename: number;
  unchanged: number;
  skipped: number;
  /** 상한에 걸려 일부만 훑었는지. 참이면 폴더를 나눠서 돌려야 한다. */
  truncated: boolean;
  items: CleanupPlanItem[];
};

export type CleanupResult = {
  scanned: number;
  renamed: number;
  unchanged: number;
  skipped: number;
  failed: number;
  rowsUpdated: number;
};

export function useCleanupPlan() {
  return useMutation({
    mutationFn: (request: CleanupRequest) =>
      apiFetch<CleanupPlan>('/v1/admin/docs-cleanup/plan', { method: 'POST', body: request }),
  });
}

export function useCleanupApply() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CleanupRequest) =>
      apiFetch<CleanupResult>('/v1/admin/docs-cleanup/apply', { method: 'POST', body: request }),
    onSuccess: () => {
      // 이름이 바뀐 뒤에는 목록이 달라져 있다. 안 비우면 옛 이름이 그대로 보여 "안 됐다" 로 읽힌다.
      void queryClient.invalidateQueries({ queryKey: ['docs-browse-s3'] });
      void queryClient.invalidateQueries({ queryKey: ['docs-folder-stats-s3'] });
    },
  });
}

export function nasBrowseQuery(connectionId: string, path: string) {
  return {
    queryKey: ['docs-browse-nas', connectionId, path] as const,
    queryFn: () =>
      apiFetch<DocumentListing>(
        `/v1/documents/browse/nas/${connectionId}${path ? `?path=${encodeURIComponent(path)}` : ''}`,
      ),
  };
}

// ── NAS 묶음 (DOC-142 §3 · V610) ──────────────────────────────────────────────

/** 이 사람이 열 수 있는 멤버가 있는 묶음. 소속은 멤버의 것이다. */
export type NasBrowsePool = {
  id: string;
  name: string;
  siteId?: string | null;
  departmentId?: string | null;
  memberIds: string[];
};

export function useBrowsableNasPools() {
  return useQuery({
    queryKey: ['nas-pools-browsable'] as const,
    queryFn: () => apiFetch<NasBrowsePool[]>('/v1/documents/browse/nas-pools'),
  });
}

/** 묶음의 같은 폴더를 멤버마다 열어 합친 목록. 파일마다 docRef 가 실제 NAS 를 가리킨다. */
export function nasPoolBrowseQuery(poolId: string, path: string) {
  return {
    queryKey: ['docs-browse-nas-pool', poolId, path] as const,
    queryFn: () =>
      apiFetch<DocumentListing>(
        `/v1/documents/browse/nas-pools/${poolId}${path ? `?path=${encodeURIComponent(path)}` : ''}`,
      ),
  };
}

export function useNasPoolBrowse(poolId: string | null, path: string) {
  return useQuery({ ...nasPoolBrowseQuery(poolId ?? '', path), enabled: !!poolId });
}

/** `NAS:{연결}:{경로}` 에서 연결 id. 묶음 목록의 파일이 어느 NAS 에 있는지 여기서 읽는다. */
export function nasConnectionOfDocRef(docRef: string): string | null {
  const match = /^NAS:([0-9a-f-]{36}):/i.exec(docRef);
  return match?.[1] ?? null;
}

/** 관리용 묶음(SUPER_ADMIN · 저장공간 분석 칸). */
export type NasPoolAdmin = { id: string; name: string; members: Array<{ connectionId: string; name: string }> };

const NAS_POOLS_ADMIN_KEY = ['nas-pools-admin'] as const;

export function useNasPoolsAdmin(enabled: boolean) {
  return useQuery({
    queryKey: NAS_POOLS_ADMIN_KEY,
    queryFn: () => apiFetch<NasPoolAdmin[]>('/v1/admin/storage-usage/pools'),
    enabled,
  });
}

export function useSaveNasPool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, connectionIds }: { id: string | null; name: string; connectionIds: string[] }) =>
      apiFetch<NasPoolAdmin[]>(id ? `/v1/admin/storage-usage/pools/${id}` : '/v1/admin/storage-usage/pools', {
        method: id ? 'PUT' : 'POST',
        body: { name, connectionIds },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NAS_POOLS_ADMIN_KEY });
      void queryClient.invalidateQueries({ queryKey: ['nas-pools-browsable'] });
    },
  });
}

export function useDeleteNasPool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<NasPoolAdmin[]>(`/v1/admin/storage-usage/pools/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NAS_POOLS_ADMIN_KEY });
      void queryClient.invalidateQueries({ queryKey: ['nas-pools-browsable'] });
    },
  });
}

export function useS3Browse(prefix: string, enabled = true) {
  return useQuery({ ...s3BrowseQuery(prefix), enabled });
}

export function useNasBrowse(connectionId: string | null, path: string) {
  return useQuery({
    ...nasBrowseQuery(connectionId ?? '', path),
    queryKey: ['docs-browse-nas', connectionId, path] as const,
    enabled: !!connectionId,
  });
}

/**
 * NAS 파일 이름 검색 — <b>NAS 가 찾는다</b>(Synology {@code SYNO.FileStation.Search}).
 *
 * <p>한 요청이 수십 초 걸릴 수 있다. 그래서 <b>타이핑마다 부르지 않는다</b> — 화면이 [찾기] 를
 * 누른 뒤에만 {@code keyword} 를 채워 넣는다. 자동 재조회도 끈다: 창을 다시 포커스했다고 NAS 를
 * 또 뒤지게 하면, 사람은 아무것도 안 했는데 화면이 몇십 초씩 멎는다.
 *
 * <p>다 못 찾았을 때는 오류가 아니다 — {@code available=true} 에 {@code message} 가 실려 온다
 * (백엔드 {@code DocumentListing.partial}). 화면은 결과를 <b>보여 주면서</b> 그 한 줄을 함께 적는다.
 */
export function useNasSearch(connectionId: string | null, path: string, keyword: string) {
  const needle = keyword.trim();
  return useQuery({
    queryKey: ['docs-search-nas', connectionId, path, needle] as const,
    queryFn: () =>
      apiFetch<DocumentListing>(
        `/v1/documents/browse/nas/${connectionId}/search?q=${encodeURIComponent(needle)}` +
          (path ? `&path=${encodeURIComponent(path)}` : ''),
      ),
    enabled: !!connectionId && needle.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** NAS 파일 내려받기 링크. 백엔드가 스트림을 중계하므로 세션 쿠키가 붙는 same-origin 경로를 쓴다. */
export function nasDownloadUrl(connectionId: string, path: string): string {
  return `${runtimeConfig().apiBaseUrl}/v1/documents/browse/nas/${connectionId}/download?path=${encodeURIComponent(path)}`;
}

/**
 * <b>뷰어가 원본을 읽는 same-origin URL</b>(S3·NAS 공통). 도면 뷰어는 이 URL 로만 파일을 연다.
 *
 * S3 자료에는 presigned URL({@link DocumentEntry.downloadUrl})이 있지만 <b>뷰어는 그것을 쓰지 않는다.</b>
 * presigned 는 오리진이 앱이 아니라 스토리지(MinIO)라, 브라우저가 fetch·img 로 직접 가는 순간
 * CSP(connect-src·img-src)·mixed content·CORS 세 관문에 걸린다. 2026-08-05 운영에서 IFC 뷰어가
 * 정확히 이것 때문에 죽었고, 화면에 남는 것은 원인을 알 수 없는 `Failed to fetch` 뿐이었다.
 * presign 만료(기본 600초)도 오래 켜 둔 뷰어를 끊는다.
 *
 * 내려받기 링크는 계속 presigned 를 쓴다 — 링크 클릭은 fetch 가 아니라 화면 이동이라 CSP 대상이 아니고,
 * 큰 파일 바이트가 백엔드를 안 거치는 이점이 그대로다.
 */
export function viewerFileUrl(source: 'S3' | 'NAS', ref: string, connectionId?: string | null): string {
  const params = new URLSearchParams({ source, ref });
  if (connectionId) params.set('connectionId', connectionId);
  return `${runtimeConfig().apiBaseUrl}/v1/documents/browse/file?${params.toString()}`;
}

// ── AI 벡터화 대기열 ─────────────────────────────────────────────────────────────

export type IndexItem = {
  source: 'S3' | 'NAS';
  ref: string;
  connectionId?: string | null;
  name?: string | null;
  categoryId?: string | null;
  /**
   * 소속 현장(선택). **비면 "미지정" 이지 "본사" 가 아니다.**
   *
   * 색인 워커는 S3 키(`docs/{현장슬러그}/…`)로 현장을 되짚는데, 슬러그가 어떤 현장 이름과도 맞지
   * 않으면 **색인을 거절한다**(DOC_SITE_UNRESOLVED). 예전엔 그 경우를 "본사 공용" 으로 넣었는데,
   * 그 값은 검색에서 본사 인원 전원 공개를 뜻해 현장 문서가 조용히 본사 전체에 열렸다.
   *
   * 여기서 현장을 골라 보내면 경로 해석보다 그 값이 우선한다 — 거절당한 자료를 푸는 길이다.
   */
  siteId?: string | null;
};

/**
 * 선택 자료를 벡터화 대기열에 올린다.
 *
 * <b>끝나면 조회 목록을 반드시 다시 읽는다(onSettled).</b> 서버는 행을 PENDING 으로 바꿨는데 화면은
 * 직전 응답을 그대로 들고 있으면(`main.tsx` 의 `staleTime: 30_000`) 뱃지가 안 바뀐다 — 눌러도 아무
 * 일도 안 일어난 것처럼 보이고, 정말 큐에 들어갔는지를 화면만 봐서는 알 수 없다. 실패했을 때도
 * 다시 읽는 이유는 같다: 서버 상태와 화면을 벌려 두지 않는다.
 *
 * 실패는 호출부가 {@code mutation.error} 로 받아 <b>화면에 띄워야 한다</b>. 삼키면 403(자료실 CREATE
 * 권한 없음)이 조용히 사라져 "대기중인데 안 된다" 로만 보인다.
 */
export function useEnqueueIndex() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: IndexItem[]) => apiFetch<number>('/v1/documents/index', { method: 'POST', body: { items } }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['docs-browse-s3'] });
      queryClient.invalidateQueries({ queryKey: ['docs-browse-nas'] });
    },
  });
}

// ── 문서 입력(업로드) ────────────────────────────────────────────────────────────
//
// 업로드는 두 걸음이다. prepare 로 규칙이 매긴 파일명과 S3 업로드 링크를 받고(겹치면 conflict), 그 링크로
// 파일을 직접 PUT 한 뒤 ingest 로 메타·라우팅(S3 확정 + NAS 복제)·노출·메뉴를 확정한다.

export type SiteMin = { id: string; name: string };

export function useSitesMin() {
  return useQuery({
    queryKey: ['sites-min'],
    queryFn: () => apiFetch<SiteMin[]>('/v1/sites'),
  });
}

export type VisibilityInput = {
  scopeType: 'SITE' | 'DEPARTMENT' | 'ROLE' | 'USER' | 'ALL_HQ';
  scopeRefId?: string | null;
  scopeRefCode?: string | null;
};

export type IngestPrepareResult = {
  storedName: string;
  s3Key: string;
  uploadUrl: string | null;
  overwrite: boolean;
  conflict: boolean;
  existingName: string | null;
  suggestedName: string | null;
};

export type IngestConflictStrategy = 'BUMP' | 'OVERWRITE';

export type IngestPreviewResult = {
  storedName: string;
  targets: Array<{
    type: 'S3' | 'NAS';
    /** 자리 이름 — `전사 공개` · `부서 전용 · 인사총무팀` · `현장 전용 · 청주현장` · `개인 자료`. */
    label: string;
    path: string;
    /** 그 자리를 누가 보는가. NAS 처럼 우리가 정하지 않는 자리는 null. */
    audience: string | null;
    /** 왜 그 자리인가 — 고른 값 때문인지, 소속으로 물러선 결과인지. */
    reason: string | null;
  }>;
  /**
   * 업로드가 **파일 저장 말고 또 무엇을 하는가** — 공지 초안이 선다 · 현장 서류 자리에 뜬다.
   *
   * 누른 뒤에야 드러나면 사람은 자기가 무엇을 시켰는지 모른 채 누르는 셈이다. 승인은
   * 「무슨 일이 일어나는지 보고 누르는 것」 이어야 한다.
   *
   * **`?` 인 것이 중요하다.** 옛 백엔드는 이 칸을 안 보낸다. 화면이 `.length` 를 그냥 세면
   * 그 순간 그리다 터지고, 사용자에게는 「아무 일도 안 일어난다」 로만 보인다 — 실제로 그 사고가
   * 났다(2026-09-03). 밖에서 오는 배열은 언제나 없을 수 있다고 보고 읽는다.
   */
  effects?: Array<{
    kind: 'NOTICE_DRAFT' | 'SITE_DOC_SLOT' | 'BOARD_SHARED';
    label: string;
    /** 공지 초안이면 실제로 쓰일 제목. 아니면 null. */
    title: string | null;
    /** 공지 초안이면 실제로 쓰일 본문. 아니면 null. */
    body: string | null;
    note: string | null;
  }>;
};

/**
 * 자리를 정하는 두 값. **preview 와 prepare 에 같은 값을 보내야** 미리보기와 실제 경로가 어긋나지 않는다.
 *
 * 둘 다 비면 서버가 <b>올린 사람의 소속 부서</b>로 앉힌다(`DocumentIngestService.keyFor`).
 */
/**
 * 분류를 <b>비워 보낼 수 있다.</b> AI 가 유형을 못 읽었을 때 화면이 여기서 막아 서면 파일이 저장소에
 * 들어가지도 못한다 — 안 고른 분류는 서버가 그 현장 칸(없으면 `90-000` 본사·공용)으로 정한다.
 */
type CategoryBody = { categoryId: string | null };

type PlacementBody = {
  /** 부서 전용으로 못 박을 때. 화면은 보내지 않는다 — 소속은 서버가 세션에서 안다. */
  departmentId?: string | null;
  /** 전 직원 공개(`docs/공용/`). 현장을 고른 자료에는 보내지 않는다 — 현장 폴더 밖으로 나가 버린다. */
  shared?: boolean;
  /**
   * 반영 메뉴 — **자리를 정하는 값이다.** 공지사항·사내지침에 반영할 문서는 전 직원이 보는 글의
   * 자료라 `docs/공용/` 에 앉는다(`BoardAttachmentSlot.publishingBoard`).
   *
   * 1단계에 실어야 한다. 2단계에 가서야 알면 파일은 이미 부서 폴더에 올라가 있다.
   */
  menuKeys?: string[];
};

/** 저장 전 미리보기 — 확정될 파일명과 저장 경로(S3·선택 NAS). 부수효과 없음. */
export function usePreviewIngest() {
  return useMutation({
    mutationFn: (body: PlacementBody & CategoryBody & {
      siteId: string | null;
      subjectPersonnelId?: string | null;
      originalName: string;
      /** 개정 회차 — 파일명 규칙의 `VERSION` 토큰. 비면 그 자리를 건너뛴다. */
      version?: string | null;
      /** 파일명 규칙의 `EMPLOYEE` 토큰. 개인 자료면 비워도 귀속 인력명으로 서버가 채운다. */
      employeeName?: string | null;
      /** AI 요약 — 서버가 이걸로 공지 초안의 제목·본문을 지어 `effects` 에 실어 돌려준다. */
      summary?: string | null;
      nasConnectionIds: string[];
    }) => apiFetch<IngestPreviewResult>('/v1/documents/ingest/preview', { method: 'POST', body }),
  });
}

// ── 개인 자료(인력 매핑) · 네이밍 규칙 ───────────────────────────────────────────

export type PersonnelMin = { id: string; name: string; deptSite: string | null };

/** 대량 업로드 화면의 인력 선택지(자동매칭·표시). 업로드 권한 필요. */
export function usePersonnelMin() {
  return useQuery({
    queryKey: ['documents-personnel-min'],
    queryFn: () => apiFetch<PersonnelMin[]>('/v1/documents/personnel-min'),
  });
}

/** 마이페이지 — 나에게 귀속된 개인 자료. */
export function useMyDocumentFiles() {
  return useQuery({
    queryKey: ['document-files', 'mine'],
    queryFn: () => apiFetch<DocumentFile[]>('/v1/documents/files/mine'),
  });
}

export type NamingRule = {
  tokens: string[];
  separator: string;
  dateFormat: string;
  sample: string;
  availableTokens: Array<{ token: string; label: string }>;
};

export function useNamingRule() {
  return useQuery({ queryKey: ['naming-rule'], queryFn: () => apiFetch<NamingRule>('/v1/documents/naming-rule') });
}

export function useUpdateNamingRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { tokens: string[]; separator: string; dateFormat: string }) =>
      apiFetch<NamingRule>('/v1/documents/naming-rule', { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['naming-rule'] }),
  });
}

// ── AI 문서 분석(업로드 1.5단계) ────────────────────────────────────────────────
//
// 파일을 저장소에 올린 뒤, 확정하기 전에 "이게 무슨 문서인가" 를 묻는다. 돌아오는 것은 **제안**이고
// 확정은 사람이 누른다 — 그래서 저장 경로(ingest)는 조금도 바뀌지 않는다.
// 에이전트가 꺼져 있거나 실패하면 available=false 로 오고, 화면은 수동 입력 그대로 진행한다.

/** `label` 은 에이전트의 추출 스키마가 준 이름이다(옛 에이전트는 안 보낸다 — 그때는 `key` 를 쓴다). */
export type AnalyzedField = { key: string; label?: string | null; value: string; confidence: number };

export type DocumentAnalysis = {
  available: boolean;
  /** LICENSE · DAILY_LOG · CONTRACT … / UNKNOWN */
  docType: string;
  docTypeLabel: string;
  /** A 색인형 · B 추출형 · C 발급형 */
  axis: string;
  confidence: number;
  lowConfidence: boolean;
  reason: string;
  summary: string;
  keywords: string[];
  categoryId: string | null;
  menuKeys: string[];
  /** 갱신 대상 표 — "어디가 바뀌는가" 를 사람에게 보여 준다. */
  targets: string[];
  fields: AnalyzedField[];
  guide: string;
  /** 개인 자료(자격증·경력·급여)로 보이는가. */
  personal: boolean;
  personName: string | null;
  /** 급여 등 격리 대상 — 내용을 분석하지 않는다. */
  sensitive: boolean;
  text: { kind?: string; pages?: number; chars?: number; note?: string };
  message: string;
};

/**
 * 파일을 그대로 보내 분석한다. **저장소를 거치지 않는다** — 분석은 문서를 읽는 일이지 저장소를 읽는
 * 일이 아니고, 어디에 저장할지(분류)를 정하는 것이 바로 분석의 목적이라 저장이 선행일 수 없다.
 * 여기 보낸 바이트는 저장되지 않는다(에이전트로 흘러가고 버려진다).
 */
export function useAnalyzeDocument() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return apiFetch<DocumentAnalysis>('/v1/documents/ingest/analyze', { method: 'POST', body: form });
    },
  });
}

export function usePrepareIngest() {
  return useMutation({
    mutationFn: (body: PlacementBody & CategoryBody & {
      siteId: string | null;
      subjectPersonnelId?: string | null;
      originalName: string;
      /** 개정 회차 — 파일명 규칙의 `VERSION` 토큰. 미리보기와 <b>같은 값</b>을 보내야 이름이 어긋나지 않는다. */
      version?: string | null;
      /** 파일명 규칙의 `EMPLOYEE` 토큰. 위와 같다. */
      employeeName?: string | null;
      strategy?: IngestConflictStrategy;
    }) => apiFetch<IngestPrepareResult>('/v1/documents/ingest/prepare', { method: 'POST', body }),
  });
}

/** presigned PUT 로 파일을 저장소에 직접 올린다(바이트가 백엔드를 안 거친다). */
export async function putToStorage(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, { method: 'PUT', body: file });
  if (!res.ok) {
    throw new Error(`저장소 업로드 실패 (HTTP ${res.status})`);
  }
}

export type DocumentFileTargetView = {
  targetType: 'S3' | 'NAS';
  nasConnectionId: string | null;
  storageKey: string;
  uploadStatus: 'PENDING' | 'DONE' | 'FAILED' | 'DELETED';
  uploadedAt: string | null;
  errorMessage: string | null;
};

export type DocumentFile = {
  id: string;
  categoryId: string;
  categoryCode: string | null;
  categoryName: string | null;
  siteId: string | null;
  subjectPersonnelId: string | null;
  subjectName: string | null;
  originalName: string;
  storedName: string;
  ext: string;
  sizeBytes: number | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  downloadUrl: string | null;
  createdAt: string | null;
  createdBy: string | null;
  targets: DocumentFileTargetView[];
  visibility: VisibilityInput[];
  menuKeys: string[];
};

export type IngestBody = CategoryBody & {
  siteId: string | null;
  subjectPersonnelId?: string | null;
  originalName: string;
  storedName: string;
  s3Key: string;
  sizeBytes: number | null;
  contentHash: string | null;
  overwrite: boolean;
  nasConnectionIds: string[];
  visibility: VisibilityInput[];
  menuKeys: string[];
  /**
   * 업로드 전에 돌린 분석 결과(선택). 확정 시점에 다시 분석하지 않으려고 <b>요청에 실어</b> 보낸다.
   * 지금은 세금계산서를 공무 증빙으로 잇는 데 쓴다(DOC-38 §8).
   */
  docType?: string | null;
  analysisFields?: Record<string, string> | null;
  /** AI 요약 — 공지 초안의 제목·본문이 된다. 비면 파일명이 제목이 된다. */
  summary?: string | null;
  /**
   * 반영 메뉴가 공지 게시판이면 **그 게시판에 초안을 세울 것인가.**
   *
   * 게시판 첨부 흐름도 같은 메뉴 키를 보내므로(그 글의 자료로 걸려야 한다), 메뉴만으로 정하면
   * 사람이 쓰고 있는 글 옆에 같은 내용의 초안이 하나 더 선다. 자료실 업로드 화면만 켜서 보낸다.
   */
  draftPost?: boolean;
  /**
   * 확인 모달에서 <b>사람이 고친</b> 제목·본문(V217). 이 값이 오면 서버는 요약 다듬기를 다시 걸지
   * 않는다 — 읽고 승인한 문장과 실제로 올라간 문장이 갈리면 안 된다.
   *
   * <p>비어 있으면 서버가 {@link IngestBody.summary} 로 만든다. 백엔드
   * {@code DocumentIngestRequest} 의 같은 이름 두 칸과 짝이다.
   */
  noticeTitle?: string | null;
  noticeBody?: string | null;
};

export function useIngest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IngestBody) => apiFetch<DocumentFile>('/v1/documents/ingest', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-files'] }),
  });
}

/** 소비 목록 — 그 메뉴에 걸린, 내가 볼 수 있는 문서. */
/**
 * 소비 목록. `menuKey` 를 비우면 <b>메뉴 링크를 보지 않고</b> 현장으로만 좁힌다 — 현장 조회의 서류
 * 탭처럼 반영 메뉴를 걸 화면이 없는 자리에서 쓴다(그쪽은 분류코드로 다시 거른다).
 */
export function useDocumentFiles(menuKey: string | null, siteId?: string | null, enabled = true) {
  return useQuery({
    queryKey: ['document-files', menuKey ?? null, siteId ?? null],
    queryFn: () => {
      const query = new URLSearchParams();
      if (menuKey) query.set('menuKey', menuKey);
      if (siteId) query.set('siteId', siteId);
      return apiFetch<DocumentFile[]>(`/v1/documents/files?${query.toString()}`);
    },
    enabled,
  });
}

/**
 * 현장 조회의 **서류 탭 목록** — 업로드 확인 모달의 「어느 탭을 갱신할까」 선택지.
 *
 * 탭을 고른다는 말은 **그 탭이 맡는 분류로 넣겠다**는 뜻이다(서버가 분류로 자리를 정한다).
 * 그 대응표를 화면에 박지 않고 서버에서 받는 이유는 `DocSlotCatalogController` 머리말에 있다 —
 * 표가 둘이 되면 어긋난 쪽으로 들어간 문서가 고른 탭에 안 뜨면서 아무 오류도 내지 않는다.
 */
export type DocSlotOption = { slot: string; label: string; categoryCode: string | null };

export function useDocSlots() {
  return useQuery({
    queryKey: ['pmis-doc-slots'],
    queryFn: () => apiFetch<DocSlotOption[]>('/v1/pmis/doc-slots'),
    staleTime: Infinity,        // 코드에 박힌 표다 — 세션 중에 바뀌지 않는다.
  });
}

/** 지정 결과. 셋을 구분해야 화면이 "무슨 일이 일어났는지" 를 말할 수 있다. */
export type AttachOutcome = 'ATTACHED' | 'REMAPPED' | 'UNCHANGED';

export type AttachResult = {
  documentFileId: string;
  name: string;
  outcome: AttachOutcome;
  message: string;
};

/**
 * <b>이미 저장소에 있는 파일</b>을 이 현장·이 분류의 자료로 지정한다 — 올리지 않는다.
 *
 * <p>파일은 있던 자리에 그대로 있고, 바뀌는 것은 «이 오브젝트는 이 현장의 이 분류다» 라는 사실뿐이다.
 * 이미 등재된 파일이면 분류·현장을 <b>다시 매긴다</b>(새 행을 만들지 않는다 — 같은 오브젝트를
 * 가리키는 행이 둘이면 목록과 검색이 같은 파일을 두 번 센다).
 */
export function useAttachDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      source: 'S3' | 'NAS';
      ref: string;
      connectionId: string | null;
      categoryId: string;
      siteId: string | null;
      name: string;
    }) => apiFetch<AttachResult>('/v1/documents/files/attach', { method: 'POST', body }),
    onSuccess: () => {
      // 탭 목록(document-files)과 조회 목록(색인 뱃지)이 함께 바뀐다.
      queryClient.invalidateQueries({ queryKey: ['document-files'] });
      queryClient.invalidateQueries({ queryKey: ['docs-browse-s3'] });
      queryClient.invalidateQueries({ queryKey: ['docs-browse-nas'] });
    },
  });
}

export function useDeleteDocumentFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/documents/files/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-files'] }),
  });
}
